

import React from "react";
import { ApiResponseData, BaseService, ILeaderboard, IProgram, OmneHostService, SanitizedResponse } from "../../../services";
import { TournamentCard } from "./Tournaments";
import styles from "../assets/style.module.css";
import { i18nManager } from "../../../i18n";
import { Classname, Paginator, Scheme, TypeChecker } from "@rivtn/noseur";
import { Utils } from "../../../utils";

type LeaderboardType = 'teams' | 'players';
export type CountryFilter = 'All' | 'Nigeria' | 'Kenya';

interface RankEntry {
    id: number;
    name: string;
    points: number;
    winRate: string;
    matches: number;
    avatar: string;
    country: 'Nigeria' | 'Kenya';
}


const TEAM_RANKS: RankEntry[] = [
    { id: 1, name: 'Lagos Lions', points: 4800, winRate: '82%', matches: 120, avatar: '🦁', country: 'Nigeria' },
    { id: 2, name: 'Nairobi Knights', points: 4250, winRate: '75%', matches: 115, avatar: '🛡️', country: 'Kenya' },
    { id: 3, name: 'Delta Phantoms', points: 3900, winRate: '68%', matches: 140, avatar: '👻', country: 'Nigeria' },
    { id: 4, name: 'Mombasa Sharks', points: 3100, winRate: '60%', matches: 90, avatar: '🦈', country: 'Kenya' },
];

const PLAYER_RANKS: RankEntry[] = [
    { id: 1, name: 'ShadowX', points: 1250, winRate: '91%', matches: 45, avatar: '👤', country: 'Nigeria' },
    { id: 2, name: 'KenyanPulse', points: 1100, winRate: '84%', matches: 38, avatar: '⚡', country: 'Kenya' },
    { id: 3, name: 'NaijaGhost', points: 950, winRate: '79%', matches: 50, avatar: '☄️', country: 'Nigeria' },
    { id: 4, name: 'SafariSniper', points: 880, winRate: '72%', matches: 42, avatar: '🎯', country: 'Kenya' },
];

export const CustomDropdown = ({ selected, options: _options, onSelect }: any) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const dropdownRef = React.useRef<HTMLDivElement>(null);
    const optionsAreList = !TypeChecker.isString(_options?.[0]);
    const options = (optionsAreList ? _options : _options?.map?.((o: any) => ([o, o])))

    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className={styles["dropdown-container"]} ref={dropdownRef}>
            <button className={styles["dropdown-trigger"]} onClick={() => setIsOpen(!isOpen)}>
                <span>{options.find((o: any) => o[0] === selected)?.[1] ?? selected}</span>
                <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: '0.2s', marginLeft: 20 }}
                >
                    <path d="M6 9l6 6 6-6" />
                </svg>
            </button>
            {isOpen && (
                <div className={styles["dropdown-menu"]}>
                    {options?.map((opt: string[]) => {
                        let label = opt[1], value = opt[0];
                        return (
                            <div
                                key={value}
                                className={`${styles["dropdown-item"]} ${selected === value ? styles["active"] : ''}`}
                                onClick={() => {
                                    onSelect(value);
                                    setIsOpen(false);
                                }}
                            >
                                {label}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export interface LeaderboardProps {
    program: IProgram;
}

// TODO leader board for other datas like offside etc
function Leaderboard(props: LeaderboardProps) {

    const { program } = props;
    const labels = i18nManager.Labels;
    const omnehostService = OmneHostService.getInstance();
    const [type, setType] = React.useState<LeaderboardType>('teams');
    const [country, setCountry] = React.useState<CountryFilter>('All');
    const [programLeaderboard, setProgramLeaderboard] = React.useState<ApiResponseData<ILeaderboard>>();
    const rawData = type === 'teams' ? TEAM_RANKS : PLAYER_RANKS;

    React.useEffect(fetchLeaderboard, []);

    const filteredData = React.useMemo(() => {
        if (country === 'All') return rawData;
        return rawData.filter(entry => entry.country === country);
    }, [rawData, country]);

    return (<div className={styles["container"]} style={{ paddingTop: '100px' }} ref={(r) => r?.scrollIntoView()}>
        <h1 style={{ fontSize: '48px', fontWeight: 900, fontStyle: 'italic', textTransform: 'uppercase' }}>Hall of Fame</h1>

        <div style={{ display: 'flex', gap: '40px', flexWrap: 'wrap', marginBottom: '40px', alignItems: 'flex-end' }}>
            <div>
                <div style={{ fontSize: '11px', fontWeight: '800', color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '12px' }}>Category</div>
                <div style={{ display: 'flex', gap: '8px', background: 'var(--bg-surface)', padding: '6px', borderRadius: '14px', border: '1px solid var(--border-color)' }}>
                    <button
                        onClick={() => setType('teams')}
                        className={`${styles["nav-btn"]} ${type === 'teams' ? styles["active"] : ''}`}
                        style={{ padding: '8px 24px', borderRadius: '10px', fontSize: '10px' }}
                    >
                        Teams
                    </button>
                    <button
                        onClick={() => setType('players')}
                        className={`${styles["nav-btn"]} ${type === 'players' ? styles["active"] : ''}`}
                        style={{ padding: '8px 24px', borderRadius: '10px', fontSize: '10px' }}
                    >
                        Players
                    </button>
                </div>
            </div>

            <div>
                <div style={{ fontSize: '11px', fontWeight: '800', color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '12px' }}>Region</div>
                <CustomDropdown selected={country} options={['All', 'Nigeria', 'Kenya']} onSelect={setCountry} />
            </div>
        </div>

        <div className={styles["table-box"]}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                <thead>
                    <tr>
                        <th style={{ padding: '24px 32px', fontSize: '11px', textTransform: 'uppercase', color: 'var(--text-secondary)', borderBottom: '1px solid var(--border-color)' }}>Rank</th>
                        <th style={{ padding: '24px 32px', fontSize: '11px', textTransform: 'uppercase', color: 'var(--text-secondary)', borderBottom: '1px solid var(--border-color)' }}>Name</th>
                        <th style={{ padding: '24px 32px', fontSize: '11px', textTransform: 'uppercase', color: 'var(--text-secondary)', borderBottom: '1px solid var(--border-color)' }}>Matches</th>
                        <th style={{ padding: '24px 32px', fontSize: '11px', textTransform: 'uppercase', color: 'var(--text-secondary)', borderBottom: '1px solid var(--border-color)' }}>Win Rate</th>
                        <th style={{ padding: '24px 32px', fontSize: '11px', textTransform: 'uppercase', color: 'var(--text-secondary)', borderBottom: '1px solid var(--border-color)', textAlign: 'right' }}>Points</th>
                    </tr>
                </thead>
                <tbody>
                    {programLeaderboard?.content?.map((entry, idx) => (
                        <tr key={entry.external_id}>
                            <td style={{ padding: '24px 32px', borderBottom: '1px solid var(--border-color)' }}>
                                <div
                                    className={styles["rank-badge"]}
                                    style={{ color: idx === 0 ? '#ffd700' : idx === 1 ? '#c0c0c0' : idx === 2 ? '#cd7f32' : 'var(--text-secondary)' }}
                                >
                                    #{idx + 1}
                                </div>
                            </td>
                            <td style={{ padding: '24px 32px', borderBottom: '1px solid var(--border-color)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                    <img style={{ height: 45, width: 45, marginRight: 10, borderRadius: "100%" }} src={entry.participant.image_url} />
                                    <div>
                                        <div style={{ fontWeight: 900, textTransform: 'uppercase', fontStyle: 'italic', fontSize: '18px' }}>{entry.participant.name}</div>
                                        <div className={styles["country-tag"]}>{i18nManager.queryCountryCurrency(entry.participant.country_code, "country_code", "country_name")}</div>
                                    </div>
                                </div>
                            </td>
                            <td style={{ padding: '24px 32px', borderBottom: '1px solid var(--border-color)', color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{entry.stats.matches_total_count}</td>
                            <td style={{ padding: '24px 32px', borderBottom: '1px solid var(--border-color)', color: 'var(--accent-color)', fontWeight: 900 }}>{entry.stats.win_rate}%</td>
                            <td style={{ padding: '24px 32px', borderBottom: '1px solid var(--border-color)', textAlign: 'right', fontWeight: 900, fontSize: '24px', fontStyle: 'italic' }}>{entry.points.toLocaleString()}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>

        {(!programLeaderboard?.content) && (Utils.loadingState())}

        <Paginator scheme={Scheme.LIGHT} style={{ marginTop: 50 }} rowsPerPage={programLeaderboard?.size} totalRecords={programLeaderboard?.total_elements} template={{
            activePageLabel: () => "",
            nextPageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, "fa fa-angle-right")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
            previousPageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, "fa fa-angle-left")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
            lastPageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, "fa fa-angle-double-right")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
            firstPageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, "fa fa-angle-double-left")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
            pageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, (option.active ? styles["active"] : null))} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick}>{option.label}</button>),
        }} />
    </div>);

    function fetchLeaderboard() {
        omnehostService.queryProgramLeaderboard(program.public_id, {
            sort: "updated_at,ASC",
            status__in: ["UPCOMING", "ONGOING", "ENDED"],
        }).then(async ({ apiResponse }: SanitizedResponse<ILeaderboard>) => {
            setProgramLeaderboard(apiResponse.data!);
        }).catch(BaseService.reportError);
    }

}

export default Leaderboard;